import { useMemo, useState } from 'react';
import { useMarkSignup, useWaitlist, type WaitlistFilters } from '../lib/queries';
import Spinner from '../components/Spinner';
import type { WaitlistRow } from '../lib/types';

const STATUS_LABEL: Record<string, string> = {
  pending: 'Pendente',
  notified: 'Notificado',
  confirmed: 'Confirmado',
  unsubscribed: 'Saiu',
};

export default function Waitlist() {
  const [filters, setFilters] = useState<WaitlistFilters>({});
  const { data, isLoading, error } = useWaitlist(filters);
  const mark = useMarkSignup();

  const rows = data ?? [];

  function exportCsv() {
    const csv = toCsv(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bebecare-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h1 className="h3 mb-0">Waitlist</h1>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={exportCsv}
          disabled={rows.length === 0}
        >
          <i className="bi bi-download me-1" /> Exportar CSV ({rows.length})
        </button>
      </div>

      <Filters value={filters} onChange={setFilters} />

      {isLoading && <Spinner />}
      {error && (
        <div className="alert alert-danger">
          Erro: {String((error as Error).message)}
        </div>
      )}

      <div className="card shadow-sm">
        <div className="table-responsive">
          <table className="table table-sm align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>Quando</th>
                <th>Email / Nome</th>
                <th>Produto</th>
                <th>Persona</th>
                <th>Origem</th>
                <th>Practice</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <Row key={r.id} row={r} onAction={mark.mutate} />
              ))}
              {!isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center text-muted py-4">
                    Nenhum inscrito com esses filtros.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Filters({
  value,
  onChange,
}: {
  value: WaitlistFilters;
  onChange: (f: WaitlistFilters) => void;
}) {
  const set = (patch: Partial<WaitlistFilters>) =>
    onChange({ ...value, ...patch });

  return (
    <div className="card shadow-sm mb-3">
      <div className="card-body py-3">
        <div className="row g-2">
          <div className="col-md-3">
            <input
              type="search"
              className="form-control form-control-sm"
              placeholder="Buscar email / nome"
              value={value.search ?? ''}
              onChange={(e) => set({ search: e.target.value || undefined })}
            />
          </div>
          <div className="col-md-2">
            <select
              className="form-select form-select-sm"
              value={value.product ?? ''}
              onChange={(e) => set({ product: e.target.value || undefined })}
            >
              <option value="">Todos produtos</option>
              <option value="bebecare">bebecare</option>
              <option value="escola">escola</option>
            </select>
          </div>
          <div className="col-md-2">
            <select
              className="form-select form-select-sm"
              value={value.status ?? ''}
              onChange={(e) =>
                set({
                  status: (e.target.value || undefined) as WaitlistFilters['status'],
                })
              }
            >
              <option value="">Todos status</option>
              <option value="pending">Pendentes</option>
              <option value="notified">Notificados</option>
              <option value="confirmed">Confirmados</option>
              <option value="unsubscribed">Saíram</option>
            </select>
          </div>
          <div className="col-md-2">
            <select
              className="form-select form-select-sm"
              value={value.persona ?? ''}
              onChange={(e) => set({ persona: e.target.value || undefined })}
            >
              <option value="">Todas personas</option>
              <option value="pais">pais</option>
              <option value="pediatra">pediatra</option>
              <option value="clinica">clinica</option>
              <option value="cuidador">cuidador</option>
            </select>
          </div>
          <div className="col-md-2">
            <input
              className="form-control form-control-sm"
              placeholder="referrer_slug"
              value={value.referrer_slug ?? ''}
              onChange={(e) =>
                set({ referrer_slug: e.target.value || undefined })
              }
            />
          </div>
          <div className="col-md-1 d-grid">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => onChange({})}
            >
              Limpar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({
  row,
  onAction,
}: {
  row: WaitlistRow;
  onAction: (input: {
    id: string;
    action: Parameters<ReturnType<typeof useMarkSignup>['mutate']>[0]['action'];
  }) => void;
}) {
  const created = useMemo(() => new Date(row.created_at), [row.created_at]);

  return (
    <tr>
      <td>
        <div className="small">{created.toLocaleDateString('pt-BR')}</div>
        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
          {created.toLocaleTimeString('pt-BR', {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </div>
      </td>
      <td>
        <div className="fw-semibold small">{row.email}</div>
        {row.name && (
          <div className="text-muted" style={{ fontSize: '0.78rem' }}>
            {row.name}
          </div>
        )}
        {row.phone && (
          <div className="text-muted" style={{ fontSize: '0.74rem' }}>
            {row.phone}
          </div>
        )}
      </td>
      <td>
        <span className="badge bg-light text-dark border">{row.product}</span>
      </td>
      <td className="small">{row.persona ?? '—'}</td>
      <td className="small">{row.source ?? '—'}</td>
      <td className="small">
        {row.practice_name ?? (row.referrer_slug ? `(${row.referrer_slug})` : '—')}
      </td>
      <td>
        <span className={`badge badge-status-${row.status}`}>
          {STATUS_LABEL[row.status] ?? row.status}
        </span>
      </td>
      <td>
        <div className="btn-group btn-group-sm">
          {row.status === 'pending' && (
            <button
              className="btn btn-outline-primary"
              title="Marcar como notificado"
              onClick={() => onAction({ id: row.id, action: 'notify' })}
            >
              <i className="bi bi-send" />
            </button>
          )}
          {row.status === 'notified' && (
            <button
              className="btn btn-outline-success"
              title="Marcar como confirmado"
              onClick={() => onAction({ id: row.id, action: 'confirm' })}
            >
              <i className="bi bi-check2-circle" />
            </button>
          )}
          {row.status !== 'unsubscribed' && (
            <button
              className="btn btn-outline-secondary"
              title="Marcar como saiu"
              onClick={() => onAction({ id: row.id, action: 'unsubscribe' })}
            >
              <i className="bi bi-x-circle" />
            </button>
          )}
          {row.status === 'unsubscribed' && (
            <button
              className="btn btn-outline-secondary"
              title="Reativar"
              onClick={() => onAction({ id: row.id, action: 'reactivate' })}
            >
              <i className="bi bi-arrow-clockwise" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

function toCsv(rows: WaitlistRow[]): string {
  const headers = [
    'created_at',
    'product',
    'email',
    'name',
    'phone',
    'persona',
    'source',
    'referrer_slug',
    'practice_name',
    'status',
    'consent_marketing',
    'admin_notes',
  ];

  const escape = (v: unknown) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const lines = [
    headers.join(','),
    ...rows.map((r) =>
      [
        r.created_at,
        r.product,
        r.email,
        r.name,
        r.phone,
        r.persona,
        r.source,
        r.referrer_slug,
        r.practice_name,
        r.status,
        r.consent_marketing,
        r.admin_notes,
      ]
        .map(escape)
        .join(','),
    ),
  ];

  return lines.join('\n');
}
